import ChannelContext from '@jetshop/core/components/ChannelContext';
import ConfigSwitch from '@jetshop/core/components/ConfigSwitch';
import OptionSwitch from '@jetshop/core/components/OptionSwitch';
import ProductConfigurationContext from '@jetshop/core/components/ProductConfigurationProvider/ProductConfigurationContext';
import ProductVariationContext from '@jetshop/core/components/ProductVariationProvider/ProductVariationContext';
import t from '@jetshop/intl';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { Form } from 'formik';
import get from 'lodash.get';
import React, { useContext } from 'react';
import styled from 'react-emotion';
import Helmet from 'react-helmet-async';
import MaxWidth from '../Layout/MaxWidth';
import { Price } from '../Price';
import UIButton from '../ui/Button';
import InputWithLabel from '../Forms/Input';
import AddToCartForm from './AddToCart/AddToCartForm';
import { Campaigns } from './Campaigns';
import ImageContainer from './Images/ImageContainer';
import ProductInfo, { ProductSection } from './ProductInfo';
import ProductInfoAccordion from './ProductInfoAccordion';
import ProductPageLoadingState from './ProductPageLoadingState';
import RelatedProducts from './RelatedProducts';
import NotifyWhenBack from './StockStatus/NotifyWhenBack';
import StockStatusIndicator from './StockStatus/StockStatusIndicator';
import { Container } from './styledComponents';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import GeometryTablePageQuery from './GeometryTablePage.gql';
import SizeGuidePageQuery from './SizeGuidePage.gql';
import Measurement from './SeatAdjustment/Measurement';

import Image1 from './SeatAdjustment/1.jpg';
import Image2 from './SeatAdjustment/2.jpg';
import Image3 from './SeatAdjustment/3.jpg';
import DefaultPicker from './DefaultPicker';
import WebBonus from './WebBonus';
import AddThis from './AddThis';
import Releware from './Releware';
import BackLink from './BackLink';
import { useQuery } from 'react-apollo';
import { useSettings } from '../Settings';

const StyledBreadcrumbs = styled(Breadcrumbs)`
  li,
  li a {
    font-size: 0.85rem;
    color: #85919b;
    text-decoration: none;
  }

  padding: 0 1rem;
`;

const Button = styled(UIButton)`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.header.cartButton};
  border: 1px solid ${({ theme }) => theme.colors.header.cartButton};
  height: 45px;
  padding: 0.625rem 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 900;
  font-size: 14px;
  border-radius: 2px;
  &:hover {
    background-color: ${({ theme }) => theme.colors.header.cartButtonHover};
    border: 1px solid ${({ theme }) => theme.colors.header.cartButtonHover};
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
  ${({ theme }) => theme.below.md} {
    align-items: center;
  }
`;

const QuantityAddToCartWrapper = styled('div')`
  display: flex;
  width: 100%;
  align-items: flex-end;
`;

const ProductContainer = styled('div')`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding-top: 1rem;
  h1 {
    font-size: 1.5rem;
    line-height: 1.25;
    + h2 {
      font-weight: normal;
      font-size: 0.875rem;
      margin-bottom: 0.5em;
    }
  }
  .customer-comment-input-wrapper {
    .customer-comment-input {
      background: #fcfcfc;
      height: 44px;
    }
    .status-wrapper {
      top: -39px;
    }
    label {
      font-weight: 600;
      font-size: 14px;
    }
  }
  .quantity-input-wrapper {
    label {
      display: none;
    }

    margin-bottom: 0rem;
    margin-right: 10px;
    width: 45px;
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type='number'] {
      -moz-appearance: textfield; /* Firefox */
    }
    input {
      width: 45px;
      height: 45px;
      font-size: 0.875rem;
      text-align: center;
      padding: 0px;
      margin-bottom: 0px;
      background: #fff;
    }
  }
  .article-number {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.mediumgrey};
    margin: 1.25em 0;
    padding-bottom: 0.75rem;
    border-bottom: 0.5px solid #b3b3b3;

    .number {
      color: #878787;
      padding-left: 0.5rem;
    }
  }
  .short-description {
    line-height: 20px;
    font-size: 0.875rem;
    margin-bottom: 1rem;

    p {
      margin-bottom: 1rem;
    }
  }
  .product-details {
    margin: 0 auto;
    max-width: 30rem;
    padding: 1rem;

    ${({ theme }) => theme.above.md} {
      background: #fff;
      align-items: flex-start;
      padding: 2rem;
    }

    h1 {
      font-size: 28px;
      font-weight: bold;
      text-transform: uppercase;
    }

    .price,
    .new-price,
    .old-price {
      display: inline-block;
    }
    .price,
    .new-price {
      font-size: 1.9rem;
      line-height: 2.875rem;
      font-weight: bold;
    }
    .new-price {
      margin-right: 8px;
      color: ${({ theme }) => theme.colors.red};
    }
    .old-price {
      color: ${({ theme }) => theme.colors.mediumgrey};
    }
  }

  .stock-status-indicator {
    padding: 0.75rem 0;
    text-align: center;
    margin-bottom: 0.25rem;
  }

  .web-bonus {
    font-weight: normal;
    text-align: left;
    padding-top: 1rem;

    > div > div {
      display: inline;
    }

    .price {
      line-height: 1;
      display: inline;
      font-size: 12px;
    }
  }

  .add-this {
    text-align: center;
    max-width: 30rem;
    margin: 1rem auto;
  }

  ${({ theme }) => theme.below.md} {
    .gallery {
      background: #fff;
    }
  }
`;

const SubHeader = styled('div')`
  line-height: 1.5;
  font-weight: bold;
  margin-bottom: 1.25rem;
`;

const SubHeading = styled('h2')`
  margin-bottom: 12px;
  font-size: 16px;
  line-height: normal;
  letter-spacing: 0.03em;
  color: rgba(0, 0, 0, 0.4);
  letter-spacing: 0.03em;
  width: 100%;
`;

const Measurements = styled('div')`
  display: flex;
  flex-direction: row;
  margin: -0.5rem -0.5rem 0;
`;

const MobilePaddingWrapper = styled('div')`
  padding: 0 1rem;
  width: 100%;
  ${({ theme }) => theme.below.md} {
    display: flex;
    align-items: center;
    flex-direction: column;
  }
`;

const StyledBackLink = styled(BackLink)`
  padding: 1rem;
  width: 100%;
`;

function getBrand(product) {
  const field = product.customFields.find(({ key }) => key === 'brand');
  if (field) {
    return field.stringValue;
  }
}

const ProductMeta = ({ product, selectedVariation }) => {
  const { selectedChannel } = useContext(ChannelContext);

  const prod = selectedVariation || product;
  const brand = getBrand(product);

  return (
    <Helmet>
      <meta property="og:title" content={product.name} />
      <meta
        property="product:original_price:amount"
        content={prod.price.incVat}
      />
      <meta
        property="product:price:amount"
        content={prod.previousPrice.incVat}
      />
      <meta
        property="og:price:currency"
        content={selectedChannel.currency.id}
      />
      {product.images.length > 0 && (
        <meta property="og:image" content={product.images[0].url} />
      )}
      <meta
        property="product:availability"
        content={prod.stockStatus.buyable ? 'instock' : 'oos'}
      />
      {brand && <meta property="product:brand" content={brand} />}
      <meta
        property="product:category"
        content={product.canonicalCategory && product.canonicalCategory.name}
      />
      <meta
        property="og:url"
        content={`${selectedChannel.url}${product.primaryRoute.path}`}
      />
    </Helmet>
  );
};

// These fields should not show up in specifications
const specialFields = ['geometry-table', 'size-guide'];

const measurementData = {
  'Mina mått: Totallängd': Image1,
  'Mina mått: Underarm': Image2,
  'Mina mått: Gren': Image3
};

const Product = ({ result: { data, loading }, product, cacheHit }) => {
  const { selectedVariation } = useContext(ProductVariationContext);
  const configurationContext = useContext(ProductConfigurationContext);
  const stockStatus = useStockStatus(selectedVariation || product);
  const price = useDynamicPrice(product);

  const productImages = get(product, 'images', []);
  const images = selectedVariation
    ? (selectedVariation.images || []).concat(productImages)
    : productImages;
  const customFields = get(product, 'customFields', []);

  const showGeometryTable = customFields.find(
    field => field.key === 'geometry-table' && field.boolValue === true
  );
  const showSizeGuide = customFields.find(
    field => field.key === 'size-guide' && field.boolValue === true
  );

  const { data: geometryTablePage } = useQuery(GeometryTablePageQuery, {
    skip: !showGeometryTable
  });
  const { data: sizeGuidePage } = useQuery(SizeGuidePageQuery, {
    skip: !showSizeGuide
  });

  const { webBonus } = useSettings();

  // Return early if there is no cache hit
  if (!cacheHit)
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );

  const parents = getParentOrCanonicalRoute(
    get(data, 'route.parents'),
    product
  );
  const relatedProducts = get(product, 'relatedProducts', []);
  const outOfStockNotify = stockStatus.status === 'notifyWhenBack';

  const specificationFields = customFields.filter(
    field => specialFields.indexOf(field.key) === -1
  );
  // .sort((a, b) => {
  //   if (a.title > b.title) return 1;
  //   else if (b.title > a.title) return -1;
  //   else return 0;
  // });

  const allComments = get(product, 'customerComments', []);
  const measurementComments = allComments.filter(
    comment => comment.name.indexOf('Mina mått:') === 0
  );
  const otherComments = allComments.filter(
    comment => comment.name.indexOf('Mina mått:') !== 0
  );

  const hasVariants = product.variants && product.variants.values.length > 0;

  return (
    <ProductContainer>
      <AddToCartForm>
        <MaxWidth>
          <StyledBreadcrumbs
            parents={parents}
            breadcrumbText={product.breadcrumbText}
          />
        </MaxWidth>
        <Container>
          <ProductMeta {...{ product, selectedVariation }} />
          <ProductSection>
            {images && (
              <ImageContainer
                className="gallery"
                images={images}
                badges={product.badges}
                key={(selectedVariation || product).articleNumber}
              />
            )}
            <Above
              breakpoint="md"
              render={() => (
                <ProductInfo
                  description={product.description}
                  specifications={specificationFields}
                  geometryTablePage={geometryTablePage}
                  sizeGuidePage={sizeGuidePage}
                />
              )}
            />
          </ProductSection>
          <ProductSection>
            <div className="product-details">
              <header>
                <SubHeading
                  dangerouslySetInnerHTML={{ __html: product.subName }}
                />
                <h1 data-testid="product-title">
                  {product.name || 'Loading...'}
                </h1>
              </header>

              <div
                className="short-description"
                dangerouslySetInnerHTML={{
                  __html: product.shortDescription
                }}
              />
              {product.hasConfigurations && (
                <ConfigSwitch
                  components={{
                    default: DefaultPicker
                  }}
                />
              )}
              <Price
                {...price}
                css={{
                  marginBottom: '1em',
                  opacity:
                    configurationContext && configurationContext.loading
                      ? 0.3
                      : 1
                }}
              />

              <Campaigns
                campaigns={product.campaigns}
                css={{ marginTop: '-1.5em', marginBottom: '2em' }}
              />

              <StyledForm>
                {otherComments.map(comment => {
                  if (!comment.required) {
                    // only show header (used for showing discount text etc.)
                    return (
                      <SubHeader
                        key={comment.name}
                        dangerouslySetInnerHTML={{ __html: comment.name }}
                      />
                    );
                  } else {
                    // show regular comment
                    return (
                      <InputWithLabel
                        wrapperClassName="customer-comment-input-wrapper"
                        className="customer-comment-input"
                        data-testid={`${comment.name}-input`}
                        name={`comments['${comment.name}']`}
                        label={comment.name}
                        key={comment.name}
                      />
                    );
                  }
                })}
                {measurementComments.length > 0 && (
                  <Measurements>
                    {measurementComments.map(comment => (
                      <Measurement
                        key={comment.name}
                        name={`comments[${comment.name}]`}
                        src={measurementData[comment.name]}
                      />
                    ))}
                  </Measurements>
                )}
                <OptionSwitch
                  components={{
                    default: DefaultPicker
                  }}
                />
                {!outOfStockNotify && (
                  <QuantityAddToCartWrapper>
                    <InputWithLabel
                      disableValidation
                      wrapperClassName="quantity-input-wrapper"
                      min="1"
                      type="number"
                      name="quantity"
                      label="Quantity"
                    />
                    <Button data-testid="add-to-cart" type="submit">
                      {t('Add to cart')}
                    </Button>
                  </QuantityAddToCartWrapper>
                )}
              </StyledForm>
              {outOfStockNotify && (
                <NotifyWhenBack
                  text={stockStatus.text}
                  articleNumber={(selectedVariation || product).articleNumber}
                />
              )}
              {!hasVariants && (
                <StockStatusIndicator
                  className="stock-status-indicator"
                  status={stockStatus.status}
                  text={stockStatus.text}
                />
              )}
              <div className="article-number">
                {t('Article number')}:
                <span className="number">
                  {(selectedVariation || product).articleNumber}
                </span>
              </div>
              <WebBonus
                className="web-bonus"
                webBonus={webBonus}
                price={(selectedVariation || product).price}
              />
            </div>
            <Above
              breakpoint="md"
              render={() => (
                <>
                  <div className="add-this">
                    <RelatedProducts relatedProducts={relatedProducts} />
                    <AddThis
                      url={
                        typeof window !== 'undefined'
                          ? window.location.href
                          : ''
                      }
                    />
                  </div>
                </>
              )}
            />
          </ProductSection>
          <Below
            breakpoint="md"
            render={() => (
              <>
                <ProductInfoAccordion
                  description={product.description}
                  specifications={specificationFields}
                  geometryTablePage={geometryTablePage}
                  sizeGuidePage={sizeGuidePage}
                />
                <MobilePaddingWrapper>
                  <RelatedProducts relatedProducts={relatedProducts} />
                  <AddThis
                    url={
                      typeof window !== 'undefined' ? window.location.href : ''
                    }
                  />
                </MobilePaddingWrapper>
              </>
            )}
          />
          <MobilePaddingWrapper>
            <Releware
              products={product.recommendedProducts.viewed}
              title={'Others also looked at'}
            />
            <Releware
              products={product.recommendedProducts.bought}
              title={'Others also bought'}
            />
          </MobilePaddingWrapper>
          <StyledBackLink />
        </Container>
      </AddToCartForm>
    </ProductContainer>
  );
};

export default Product;
